import type {
  GridColDef,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import { EMAIL_PULSE_COLUMN_METADATA } from './email-pulse-column-metadata';

/**
 * Creates data grid column definitions for Email Pulse reports.
 *
 * @param t - Translation function
 * @param identifierColumn - The first column (e.g., messageTitle, offerTitle)
 * @param columnKeys - Optional array of column keys to include. If not provided, all columns are included.
 * @returns Array of GridColDef objects
 *
 * @example
 * ```ts
 * const columns = createEmailPulseDataGridColumns(
 *   t,
 *   {
 *     field: 'messageTitle',
 *     headerName: t('page.emailPulse.table.messageTitle.label'),
 *     minWidth: 300,
 *     hideable: false,
 *     sortable: true,
 *     flex: 1,
 *   }
 * );
 * ```
 */
export const createEmailPulseDataGridColumns = (
  t: (key: string) => string,
  identifierColumn: GridColDef,
  columnKeys?: string[],
): GridColDef[] => {
  const keys = columnKeys || Object.keys(EMAIL_PULSE_COLUMN_METADATA);

  const commonColumns: GridColDef[] = keys.map(key => {
    const metadata = EMAIL_PULSE_COLUMN_METADATA[key];
    return {
      field: metadata.field,
      headerName: metadata.definition.getLabel(t as any),
      minWidth: metadata.minWidth,
      flex: metadata.flex,
      hideable: metadata.hideable,
      sortable: metadata.sortable,
    };
  });

  return [identifierColumn, ...commonColumns];
};

/**
 * Creates column visibility model for Email Pulse data grids.
 *
 * @param columnKeys - Optional array of column keys to include. If not provided, all columns are included.
 * @returns GridColumnVisibilityModel object
 *
 * @example
 * ```ts
 * const visibilityModel = createEmailPulseColumnVisibilityModel();
 * // Returns: { sent: true, delivered: true, ..., emailsBounced: false, ... }
 * ```
 */
export const createEmailPulseColumnVisibilityModel = (
  columnKeys?: string[],
): GridColumnVisibilityModel => {
  const keys = columnKeys || Object.keys(EMAIL_PULSE_COLUMN_METADATA);

  return keys.reduce((acc, key) => {
    const metadata = EMAIL_PULSE_COLUMN_METADATA[key];
    acc[metadata.field] = metadata.defaultVisible ?? false;
    return acc;
  }, {} as GridColumnVisibilityModel);
};
